/* global Blob, document, URL, URLSearchParams */
import { referenceWarnings } from '../ideas/referenceWarnings'
import { api } from '../../shared/api/client'
import { unwrapGingerbreadResponse } from '../gingerbread-generator/api'
import { createIdempotencyKey } from '../layout-generator/api'
import type { ImageTransform, ShapeManufacturability, ShapeManufacturabilityIssue, ShapeRounding } from '../gingerbread-generator/types'
import type {
  AiBlocker, AiBudget, AiItemWarning, AiOperation, AiPlan, AiPlanRequest, AiPlanSheet, AiPlanSlot, AiRegenerateQuote, AiRun, AiRunSlot, AiRunStatus,
  BlockerRef, ExportBlocker, ExportErrorEntry, ExportResult, ExportPreview, ExportPreviewKit, ExportPreviewSingle, ExportStatus, ExportTitles,
  Gingerbread, GingerbreadSource, GingerbreadStatus, ItemAsset, ItemError, ItemFileKind, ItemFiles, ItemMetrics,
  ItemPatch, ItemShapeRef, Job, JobCounts, JobListFilters, JobPage, JobPatch, JobStatus, JobSummary, Kit, KitFileKind, KitFiles,
  KitItem, KitPatch, KitStatus, FreeCutMode, OutlineSettings, ShapeMode,
} from './types'
import { toOutline } from './outline'

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
const text = (value: unknown): string | null => (value == null || value === '' ? null : String(value))
const num = (value: unknown): number | null => {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}
const parsedJson = (value: unknown): unknown => {
  if (typeof value !== 'string') return value
  try { return JSON.parse(value) } catch { return null }
}

const JOB_STATUSES: JobStatus[] = ['draft', 'processing', 'review', 'done', 'archived']
const ITEM_STATUSES: GingerbreadStatus[] = ['pending', 'processing', 'ready', 'failed', 'approved', 'excluded']
const ITEM_SOURCES: GingerbreadSource[] = ['upload', 'sheet', 'design', 'ai']

const person = (raw: unknown): { id: number | null; name: string | null } => {
  const value = asRecord(raw)
  return { id: num(value.id), name: text(value.name) }
}

const toCounts = (raw: unknown): JobCounts => {
  const value = asRecord(raw)
  const counts: JobCounts = {
    total: num(value.total) ?? 0, pending: num(value.pending) ?? 0, processing: num(value.processing) ?? 0,
    ready: num(value.ready) ?? 0, failed: num(value.failed) ?? 0, approved: num(value.approved) ?? 0, excluded: num(value.excluded) ?? 0,
  }
  return counts
}

const toRounding = (raw: unknown): ShapeRounding | null => {
  const value = asRecord(raw)
  if (typeof value.applied !== 'boolean') return null
  return {
    applied: value.applied, innerRadiusMm: num(value.innerRadiusMm) ?? 0, outerRadiusMm: num(value.outerRadiusMm) ?? 0,
    areaDeltaMm2: num(value.areaDeltaMm2) ?? 0, areaDeltaPct: num(value.areaDeltaPct) ?? 0,
    maxDeviationMm: num(value.maxDeviationMm) ?? 0, skippedCode: text(value.skippedCode),
  }
}

export const toManufacturability = (raw: unknown): ShapeManufacturability | null => {
  const value = asRecord(parsedJson(raw))
  if (!Object.keys(value).length) return null
  const issues = (Array.isArray(value.issues) ? value.issues : []).map((entry): ShapeManufacturabilityIssue => {
    const row = asRecord(entry); const details = asRecord(row.details)
    return { code: String(row.code ?? ''), message: String(row.message ?? ''), details: Object.keys(details).length ? details : null }
  })
  return {
    ok: value.ok == null ? issues.length === 0 : Boolean(value.ok),
    minNeckMm: num(value.minNeckMm ?? value.min_neck_mm),
    minInnerRadiusMm: num(value.minInnerRadiusMm ?? value.min_inner_radius_mm),
    issues, rounding: toRounding(value.rounding),
  }
}

export const toTransform = (raw: unknown): ImageTransform | null => {
  const value = asRecord(parsedJson(raw))
  if (!Object.keys(value).length) return null
  return {
    fitMode: value.fitMode === 'contain' ? 'contain' : 'cover',
    offsetX: num(value.offsetX) ?? 0, offsetY: num(value.offsetY) ?? 0, scale: num(value.scale) ?? 1,
    rotationDeg: num(value.rotationDeg) ?? 0, backgroundColor: text(value.backgroundColor),
    focalPoint: null,
  }
}

const assetThumbnail = (id: string) => `/api/v1/gingerbread/assets/${id}/thumbnail`

const toAsset = (raw: unknown): ItemAsset => {
  const value = asRecord(raw); const id = String(value.id ?? '')
  return { id, widthPx: num(value.widthPx) ?? 0, heightPx: num(value.heightPx) ?? 0, thumbnailUrl: text(value.thumbnailUrl) ?? assetThumbnail(id) }
}

const toShapeRef = (raw: unknown): ItemShapeRef | null => {
  const value = asRecord(raw)
  if (value.id == null) return null
  return {
    id: String(value.id), code: String(value.code ?? ''), name: String(value.name ?? value.code ?? value.id),
    kind: String(value.kind ?? ''), versionId: String(value.versionId ?? ''), version: num(value.version) ?? 1,
  }
}

const toMetrics = (raw: unknown): ItemMetrics | null => {
  const value = asRecord(parsedJson(raw))
  if (!Object.keys(value).length) return null
  return {
    widthMm: num(value.widthMm), heightMm: num(value.heightMm), sugarAreaCm2: num(value.sugarAreaCm2),
    doughAreaCm2: num(value.doughAreaCm2), doughWeightG: num(value.doughWeightG),
  }
}

const toFiles = (raw: unknown): ItemFiles | null => {
  const value = asRecord(raw)
  if (!Object.keys(value).length) return null
  return { printPngUrl: text(value.printPngUrl), previewPngUrl: text(value.previewPngUrl), cutterStlUrl: text(value.cutterStlUrl) }
}

const toError = (raw: unknown): ItemError | null => {
  const value = asRecord(parsedJson(raw))
  if (!Object.keys(value).length) return null
  const details = asRecord(value.details)
  return { code: String(value.code ?? ''), message: String(value.message ?? ''), details: Object.keys(details).length ? details : null }
}

export const toGingerbread = (raw: unknown): Gingerbread => {
  const value = asRecord(raw)
  const status = String(value.status) as GingerbreadStatus
  const source = String(value.source) as GingerbreadSource
  return {
    id: String(value.id ?? ''), jobId: String(value.jobId ?? ''), position: num(value.position) ?? 0,
    name: text(value.name),
    source: ITEM_SOURCES.includes(source) ? source : 'upload',
    status: ITEM_STATUSES.includes(status) ? status : 'pending',
    shapeMode: value.shapeMode === 'unified' ? 'unified' : 'free',
    shape: toShapeRef(value.shape),
    asset: toAsset(value.asset),
    transform: toTransform(value.transform),
    targetLongSideMm: num(value.targetLongSideMm),
    ...(value.cutMode === 'exact' || value.cutMode === 'outline' || value.cutMode === 'png' || value.cutMode === 'margin' ? { cutMode: value.cutMode } : {}),
    // Обводка имеет смысл только у выреза outline; у прочих сервис отдаёт null
    ...(value.cutMode === 'outline' ? { outline: toOutline(value.outline) } : 'outline' in value ? { outline: null } : {}),
    ...(num(value.maxLongSideMm) !== null ? { maxLongSideMm: num(value.maxLongSideMm) } : {}),
    revision: num(value.revision) ?? 1, renderedRevision: num(value.renderedRevision),
    stale: Boolean(value.stale),
    metrics: toMetrics(value.metrics),
    manufacturability: toManufacturability(value.manufacturability),
    files: toFiles(value.files),
    error: toError(value.error),
    depicted: text(value.depicted), textOnImage: text(value.textOnImage),
    aiRunId: text(value.aiRunId), aiSlot: num(value.aiSlot),
    aiWarnings: toAiItemWarnings(value.aiWarnings),
    approvedAt: text(value.approvedAt), approvedBy: value.approvedBy == null ? null : person(value.approvedBy),
    reviewComment: text(value.reviewComment),
    article: text(value.article), designCode: text(value.designCode), exportedAt: text(value.exportedAt),
    createdAt: text(value.createdAt), updatedAt: text(value.updatedAt),
  }
}

const KIT_STATUSES: KitStatus[] = ['draft', 'rendering', 'ready', 'failed', 'exported', 'archived']
const EXPORT_STATUSES: ExportStatus[] = ['none', 'exporting', 'exported', 'failed']

const toKitItem = (raw: unknown, index: number): KitItem => {
  const value = asRecord(raw)
  return {
    position: num(value.position) ?? index + 1, gingerbreadId: String(value.gingerbreadId ?? value.id ?? ''),
    name: text(value.name), previewUrl: text(value.previewUrl), weightG: num(value.weightG),
  }
}

const toKitFiles = (raw: unknown): KitFiles | null => {
  const value = asRecord(raw)
  if (!Object.keys(value).length) return null
  return {
    sheetJpgUrl: text(value.sheetJpgUrl), sheetPltUrl: text(value.sheetPltUrl), cutJpgUrl: text(value.cutJpgUrl),
    kitStlUrl: text(value.kitStlUrl), previewJpgUrl: text(value.previewJpgUrl),
  }
}

/** Размер листа: сервер может отдать [w, h], {widthMm, heightMm} или {width, height}. */
const toSizeMm = (raw: unknown): Kit['sizeMm'] => {
  const parsed = parsedJson(raw)
  const pair = Array.isArray(parsed) ? parsed : (() => { const value = asRecord(parsed); return [value.widthMm ?? value.width ?? value.w, value.heightMm ?? value.height ?? value.h] })()
  const width = num(pair[0]); const height = num(pair[1])
  return width != null && height != null && width > 0 && height > 0 ? { widthMm: width, heightMm: height } : null
}

const toFormSizeMm = (raw: unknown): Kit['formSizeMm'] => {
  const parsed = parsedJson(raw)
  if (!Array.isArray(parsed) || parsed.length < 3) return null
  const [x, y, z] = parsed.map(num)
  return x != null && y != null && z != null ? [x, y, z] : null
}

export const toKit = (raw: unknown): Kit => {
  const value = asRecord(raw)
  const status = String(value.status) as KitStatus
  const items = (Array.isArray(value.items) ? value.items : []).map(toKitItem)
  return {
    id: String(value.id ?? ''), jobId: String(value.jobId ?? ''), name: String(value.name ?? ''), title: text(value.title),
    status: KIT_STATUSES.includes(status) ? status : 'draft',
    revision: num(value.revision) ?? 1, renderedRevision: num(value.renderedRevision), stale: Boolean(value.stale),
    items, itemsCount: num(asRecord(value.counts).items) ?? items.length,
    weightNetG: num(value.weightNetG), weightGrossG: num(value.weightGrossG), sugarShare: num(value.sugarShare),
    sizeMm: toSizeMm(value.sizeMm), formSizeMm: toFormSizeMm(value.formSizeMm), files: toKitFiles(value.files), error: toError(value.error),
    article: text(value.article), designCode: text(value.designCode), lastJobId: text(value.lastJobId),
    createdAt: text(value.createdAt), updatedAt: text(value.updatedAt),
  }
}

const BLOCKER_KINDS: Record<string, BlockerRef['kind']> = {
  job: 'job', task: 'job', gingerbread: 'gingerbread', item: 'gingerbread', single: 'gingerbread', kit: 'kit', set: 'kit',
}

/** Место проблемы: объект {kind|type, id}, строка «kit:5» или только id (тип тогда по коду блокера). */
const toBlockerRef = (raw: unknown, code: string): BlockerRef | null => {
  if (raw == null || raw === '') return null
  let kindText = ''; let id: string | null = null
  if (typeof raw === 'object') {
    const value = asRecord(raw)
    kindText = String(value.kind ?? value.type ?? value.entity ?? '').toLowerCase(); id = text(value.id)
  } else {
    const parts = String(raw).split(':')
    if (parts.length > 1) { kindText = parts[0].toLowerCase(); id = parts.slice(1).join(':') || null } else id = String(raw)
  }
  const upper = code.toUpperCase()
  const kind = BLOCKER_KINDS[kindText] ?? (upper.includes('CATALOG') || upper.includes('THEME') ? 'job' : upper.includes('KIT') ? 'kit' : 'gingerbread')
  return { kind, id }
}

const toBlocker = (raw: unknown): ExportBlocker => {
  const value = asRecord(raw); const code = String(value.code ?? '')
  return { code, message: String(value.message ?? code), ref: toBlockerRef(value.ref, code) }
}

const toExportError = (raw: unknown): ExportErrorEntry => {
  const value = asRecord(raw)
  return { kind: text(value.kind), code: text(value.code), message: String(value.message ?? value.error ?? ''), ref: text(value.ref) }
}

const toExportResult = (raw: unknown): ExportResult | null => {
  const value = asRecord(parsedJson(raw))
  if (!Object.keys(value).length) return null
  return {
    finishedAt: text(value.finishedAt),
    created: (Array.isArray(value.created) ? value.created : []).map(entry => {
      const row = asRecord(entry)
      return { kind: String(row.kind ?? ''), id: String(row.id ?? ''), article: text(row.article), designCode: text(row.designCode) }
    }),
    errors: (Array.isArray(value.errors) ? value.errors : []).map(toExportError),
  }
}

export const toExportPreview = (raw: unknown): ExportPreview => {
  const value = asRecord(raw); const theme = asRecord(value.theme)
  return {
    theme: {
      exists: Boolean(theme.exists), number: text(theme.number),
      missingFields: (Array.isArray(theme.missingFields) ? theme.missingFields : []).map(String),
    },
    singles: (Array.isArray(value.singles) ? value.singles : []).map((entry): ExportPreviewSingle => {
      const row = asRecord(entry)
      return {
        gingerbreadId: String(row.gingerbreadId ?? row.id ?? ''), name: text(row.name), title: String(row.title ?? ''),
        weightNetG: num(row.weightNetG), weightGrossG: num(row.weightGrossG), exported: Boolean(row.exported), article: text(row.article),
      }
    }),
    kits: (Array.isArray(value.kits) ? value.kits : []).map((entry): ExportPreviewKit => {
      const row = asRecord(entry)
      return {
        kitId: String(row.kitId ?? row.id ?? ''), name: String(row.name ?? ''), title: String(row.title ?? ''),
        itemsCount: Array.isArray(row.items) ? row.items.length : num(row.items) ?? num(asRecord(row.counts).items) ?? 0,
        weightNetG: num(row.weightNetG), weightGrossG: num(row.weightGrossG),
        ready: Boolean(row.ready), exported: Boolean(row.exported), article: text(row.article),
      }
    }),
    blockers: (Array.isArray(value.blockers) ? value.blockers : []).map(toBlocker),
  }
}

export const toJobSummary = (raw: unknown): JobSummary => {
  const value = asRecord(raw)
  const status = String(value.status) as JobStatus
  return {
    id: String(value.id ?? ''), name: String(value.name ?? ''), mode: value.mode === 'ai' ? 'ai' : 'manual',
    status: JOB_STATUSES.includes(status) ? status : 'draft',
    themeAttribute: text(value.themeAttribute),
    counts: toCounts(value.counts),
    previewUrls: (Array.isArray(value.previewUrls) ? value.previewUrls : []).map(String).filter(Boolean),
    createdBy: person(value.createdBy),
    createdAt: text(value.createdAt), updatedAt: text(value.updatedAt),
  }
}

export const toJob = (raw: unknown): Job => {
  const value = asRecord(raw)
  const fields = asRecord(parsedJson(value.catalogFields))
  return {
    ...toJobSummary(raw),
    catalogFields: Object.keys(fields).length ? fields : null,
    brief: text(value.brief),
    revision: num(value.revision) ?? 1,
    gingerbreads: (Array.isArray(value.gingerbreads) ? value.gingerbreads : []).map(toGingerbread),
    activeRenderJobId: text(value.activeRenderJobId),
    themeNumber: text(value.themeNumber),
    exportStatus: EXPORT_STATUSES.includes(value.exportStatus as ExportStatus) ? value.exportStatus as ExportStatus : 'none',
    activeExportJobId: text(value.activeExportJobId),
    exportResult: toExportResult(value.exportResult),
    kits: (Array.isArray(value.kits) ? value.kits : []).map(toKit),
  }
}

// ИИ-генерация рисунков (этап 5): ответы гейтвея `/gingerbread/jobs/:id/ai/*`.
const RUN_STATUSES: AiRunStatus[] = ['queued', 'running', 'completed', 'failed', 'cancelled']

const toAiBlocker = (raw: unknown): AiBlocker => {
  const value = asRecord(raw)
  return { code: String(value.code ?? ''), message: String(value.message ?? value.code ?? '') }
}

const toAiBudget = (raw: unknown): AiBudget | null => {
  const value = asRecord(raw)
  if (!Object.keys(value).length) return null
  return { limitUsd: num(value.limitUsd) ?? 0, spentUsd: num(value.spentUsd) ?? 0, reservedUsd: num(value.reservedUsd) ?? 0, availableUsd: num(value.availableUsd) ?? 0 }
}

const toAiPlanSlot = (raw: unknown): AiPlanSlot => {
  const value = asRecord(raw)
  return {
    slot: num(value.slot) ?? 0, mode: value.mode === 'unified' ? 'unified' : 'free', shapeId: text(value.shapeId), shapeName: text(value.shapeName),
    widthMm: num(value.widthMm), heightMm: num(value.heightMm), longSideMm: num(value.longSideMm),
  }
}

const toAiPlanRequest = (raw: unknown): AiPlanRequest => {
  const value = asRecord(raw)
  return {
    slots: (Array.isArray(value.slots) ? value.slots : []).map((entry): AiPlanRequest['slots'][number] => {
      const row = asRecord(entry); const size = asRecord(row.longSideMm)
      return row.mode === 'unified'
        ? { mode: 'unified', shapeId: String(row.shapeId ?? ''), count: num(row.count) ?? 0 }
        : { mode: 'free', count: num(row.count) ?? 0, longSideMm: { min: num(size.min) ?? 0, max: num(size.max) ?? 0 } }
    }),
    style: String(value.style ?? ''),
    textMode: value.textMode === 'short' ? 'short' : 'none',
  }
}

export const toAiItemWarnings = (raw: unknown): AiItemWarning[] =>
  (Array.isArray(raw) ? raw : []).map(asRecord).filter(entry => typeof entry.code === 'string' && entry.code).map((entry): AiItemWarning => ({
    code: String(entry.code), message: String(entry.message ?? ''),
    ...(entry.autoRetry === true ? { autoRetry: true } : {}), ...(entry.suggestRegenerate === true ? { suggestRegenerate: true } : {}),
    ...(typeof entry.expected === 'string' ? { expected: entry.expected } : {}), ...(typeof entry.found === 'string' ? { found: entry.found } : {}),
  }))

export const toAiPlan = (raw: unknown): AiPlan => {
  const value = asRecord(raw); const plan = asRecord(value.runPlan); const operations = asRecord(plan.operations)
  const retry = asRecord(value.autoRetry)
  return {
    sheets: (Array.isArray(plan.sheets) ? plan.sheets : []).map((entry): AiPlanSheet => {
      const sheet = asRecord(entry)
      return { index: num(sheet.index) ?? 0, mode: sheet.mode === 'unified' ? 'unified' : 'free', slots: (Array.isArray(sheet.slots) ? sheet.slots : []).map(toAiPlanSlot) }
    }),
    operations: { text: num(operations.text) ?? 0, sheet: num(operations.sheet) ?? 0, extract: num(operations.extract) ?? 0, ...(num(operations.retry) ? { retry: num(operations.retry) ?? 0 } : {}) },
    estimateUsd: num(value.estimateUsd) ?? 0,
    budget: toAiBudget(value.budget),
    ...(Object.keys(retry).length ? { autoRetry: { enabled: retry.enabled === true, slots: (Array.isArray(retry.slots) ? retry.slots : []).map(Number).filter(Number.isFinite), candidates: num(retry.candidates) ?? 0 } } : {}),
    request: toAiPlanRequest(value.request),
    quote: text(value.quote), quoteExpiresAt: text(value.quoteExpiresAt),
    blockers: (Array.isArray(value.blockers) ? value.blockers : []).map(toAiBlocker),
  }
}

export const toAiRegenerateQuote = (raw: unknown): AiRegenerateQuote => {
  const value = asRecord(raw)
  return {
    estimateUsd: num(value.estimateUsd) ?? 0, budget: toAiBudget(value.budget), quote: text(value.quote), quoteExpiresAt: text(value.quoteExpiresAt),
    blockers: (Array.isArray(value.blockers) ? value.blockers : []).map(toAiBlocker),
  }
}

const toAiOperation = (raw: unknown): AiOperation => {
  const value = asRecord(raw); const error = asRecord(value.error)
  const warnings = referenceWarnings(value)
  return {
    ...(warnings.length ? { referenceWarnings: warnings } : {}),
    id: String(value.id ?? ''), type: String(value.type ?? ''), status: String(value.status ?? ''),
    sheetIndex: num(value.sheetIndex), slot: num(value.slot),
    ...(value.autoRetry === true ? { autoRetry: true } : {}),
    error: Object.keys(error).length ? { code: String(error.code ?? ''), message: String(error.message ?? '') } : null,
    actualUsd: num(value.actualUsd) ?? 0,
  }
}

const toAiRunSlot = (raw: unknown): AiRunSlot => {
  const value = asRecord(raw)
  return {
    slot: num(value.slot) ?? 0, mode: value.mode === 'unified' ? 'unified' : 'free',
    depicted: String(value.depicted ?? ''), textOnImage: String(value.textOnImage ?? ''), gingerbreadId: text(value.gingerbreadId),
  }
}

export const toAiRun = (raw: unknown): AiRun => {
  const value = asRecord(raw); const status = String(value.status) as AiRunStatus
  return {
    runId: String(value.runId ?? ''), status: RUN_STATUSES.includes(status) ? status : 'queued',
    kind: value.kind === 'regenerate' ? 'regenerate' : 'full',
    ...(value.recut === true ? { recut: true } : {}),
    estimateUsd: num(value.estimateUsd) ?? 0, actualUsd: num(value.actualUsd) ?? 0, createdAt: text(value.createdAt),
    operations: (Array.isArray(value.operations) ? value.operations : []).map(toAiOperation),
    slots: (Array.isArray(value.slots) ? value.slots : []).map(toAiRunSlot),
  }
}

const idempotent = (key: string) => ({ headers: { 'Idempotency-Key': key } })

const listParams = (filters: JobListFilters) => {
  const params = new URLSearchParams()
  if (filters.status && filters.status !== 'all') params.set('status', filters.status)
  if (filters.search?.trim()) params.set('search', filters.search.trim())
  params.set('page', String(filters.page || 1))
  params.set('limit', String(filters.limit || 20))
  return params.toString()
}

const BASE = '/gingerbread'

const saveBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url; link.download = fileName
  document.body.appendChild(link); link.click(); link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export const gingerbreadStudioAPI = {
  async listJobs(filters: JobListFilters = {}): Promise<JobPage> {
    const raw = unwrapGingerbreadResponse<unknown>(await api.get(`${BASE}/jobs?${listParams(filters)}`))
    const record = asRecord(raw)
    const rows = Array.isArray(raw) ? raw : Array.isArray(record.items) ? record.items : Array.isArray(record.jobs) ? record.jobs : []
    return { items: rows.map(toJobSummary), total: num(record.total) ?? num(asRecord(record.pagination).total) ?? rows.length }
  },
  async createJob(body: { name: string; themeAttribute?: string; catalogFields?: Record<string, unknown>; brief?: string }): Promise<Job> {
    return toJob(unwrapGingerbreadResponse(await api.post(`${BASE}/jobs`, body)))
  },
  async getJob(id: string): Promise<Job> {
    return toJob(unwrapGingerbreadResponse(await api.get(`${BASE}/jobs/${id}`)))
  },
  async patchJob(id: string, patch: JobPatch): Promise<Job> {
    return toJob(unwrapGingerbreadResponse(await api.patch(`${BASE}/jobs/${id}`, patch)))
  },
  async archiveJob(id: string): Promise<Job> {
    return toJob(unwrapGingerbreadResponse(await api.post(`${BASE}/jobs/${id}/archive`, {})))
  },
  /**
   * Пряники по загруженным ассетам: по одному на рисунок. `cutMode`/`outline` — вырез свободной фигуры (без них — exact);
   * для формы справочника их не шлём: сервер ответит 400.
   */
  async addGingerbreads(jobId: string, body: { assetIds: string[]; shapeMode: ShapeMode; shapeId?: string; cutMode?: FreeCutMode; outline?: OutlineSettings }): Promise<Gingerbread[]> {
    const raw = asRecord(unwrapGingerbreadResponse(await api.post(`${BASE}/jobs/${jobId}/gingerbreads`, body)))
    return (Array.isArray(raw.gingerbreads) ? raw.gingerbreads : []).map(toGingerbread)
  },
  /** Лист печати: сервер режет его на рисунки и заводит пряники. */
  async addSheet(jobId: string, assetId: string): Promise<Gingerbread[]> {
    const raw = asRecord(unwrapGingerbreadResponse(await api.post(`${BASE}/jobs/${jobId}/sheets`, { assetId })))
    return (Array.isArray(raw.gingerbreads) ? raw.gingerbreads : []).map(toGingerbread)
  },
  /** 202: фоновая задача обработки. Без `gingerbreadIds` — все ожидающие, с ошибкой и устаревшие. */
  async processJob(jobId: string, gingerbreadIds?: string[]): Promise<{ jobId: string; count: number }> {
    const raw = asRecord(unwrapGingerbreadResponse(await api.post(`${BASE}/jobs/${jobId}/process`, gingerbreadIds?.length ? { gingerbreadIds } : {})))
    return { jobId: String(raw.jobId ?? ''), count: num(raw.count) ?? 0 }
  },
  async patchItem(id: string, patch: ItemPatch): Promise<Gingerbread> {
    return toGingerbread(unwrapGingerbreadResponse(await api.patch(`${BASE}/items/${id}`, patch)))
  },
  async approveItem(id: string, body: { comment?: string; override?: boolean } = {}): Promise<Gingerbread> {
    return toGingerbread(unwrapGingerbreadResponse(await api.post(`${BASE}/items/${id}/approve`, body)))
  },
  async excludeItem(id: string, comment?: string): Promise<Gingerbread> {
    return toGingerbread(unwrapGingerbreadResponse(await api.post(`${BASE}/items/${id}/exclude`, comment ? { comment } : {})))
  },
  async restoreItem(id: string): Promise<Gingerbread> {
    return toGingerbread(unwrapGingerbreadResponse(await api.post(`${BASE}/items/${id}/restore`, {})))
  },
  async deleteItem(id: string): Promise<void> {
    await api.delete(`${BASE}/items/${id}`)
  },
  /** Свободная форма пряника → справочник (активная, с именем). Возвращает id формы, если сервис его отдал. */
  async promoteShape(id: string, name: string): Promise<{ id: string | null; name: string }> {
    const raw = asRecord(unwrapGingerbreadResponse(await api.post(`${BASE}/items/${id}/promote-shape`, { name })))
    return { id: text(raw.id), name: String(raw.name ?? name) }
  },
  /** Скачивание печати или формы под тесто авторизованным запросом. */
  async downloadFile(id: string, kind: ItemFileKind, fileName: string): Promise<void> {
    saveBlob((await api.get(`${BASE}/items/${id}/files/${kind}`, { responseType: 'blob' })).data as Blob, fileName)
  },

  // ИИ-генерация рисунков (этап 5). Права: план — Gingerbread:UPDATE, запуск и перегенерация — Gingerbread:APPROVE.
  /** Ключ идемпотентности подтверждения: создаётся один раз на рассчитанный план и переживает повторы клика. */
  newAiKey(scope: 'start' | 'regenerate'): string {
    return createIdempotencyKey(`gingerbread-ai-${scope}`)
  },
  async planAi(jobId: string, request: AiPlanRequest): Promise<AiPlan> {
    return toAiPlan(unwrapGingerbreadResponse(await api.post(`${BASE}/jobs/${jobId}/ai/plan`, request)))
  },
  /** 202. В теле — нормализованный запрос из ответа плана и подписанная квота; без `Idempotency-Key` гейтвей запуск не примет. */
  async startAi(jobId: string, plan: AiPlan, idempotencyKey: string): Promise<{ runId: string; reservedUsd: number }> {
    const raw = asRecord(unwrapGingerbreadResponse(await api.post(`${BASE}/jobs/${jobId}/ai/start`, { plan: plan.request, quote: plan.quote }, idempotent(idempotencyKey))))
    return { runId: String(raw.runId ?? ''), reservedUsd: num(raw.reservedUsd) ?? 0 }
  },
  async listAiRuns(jobId: string): Promise<AiRun[]> {
    const raw = unwrapGingerbreadResponse<unknown>(await api.get(`${BASE}/jobs/${jobId}/ai/runs`))
    return (Array.isArray(raw) ? raw : []).map(toAiRun)
  },
  async cancelAiRun(jobId: string, runId: string): Promise<{ status: AiRunStatus; cancelledOperations: number }> {
    const raw = asRecord(unwrapGingerbreadResponse(await api.post(`${BASE}/jobs/${jobId}/ai/runs/${runId}/cancel`, {})))
    const status = String(raw.status) as AiRunStatus
    return { status: RUN_STATUSES.includes(status) ? status : 'cancelled', cancelledOperations: num(raw.cancelledOperations) ?? 0 }
  },
  /** Смета перегенерации одного пряника (без `quote` в теле): ничего не запускает. */
  async quoteAiRegenerate(itemId: string, note: string): Promise<AiRegenerateQuote> {
    return toAiRegenerateQuote(unwrapGingerbreadResponse(await api.post(`${BASE}/items/${itemId}/ai/regenerate`, note.trim() ? { note: note.trim() } : {})))
  },
  /** 202: запуск перегенерации по подписанной смете. */
  async regenerateAi(itemId: string, note: string, quote: string, idempotencyKey: string): Promise<{ runId: string }> {
    const raw = asRecord(unwrapGingerbreadResponse(await api.post(`${BASE}/items/${itemId}/ai/regenerate`, { ...(note.trim() ? { note: note.trim() } : {}), quote }, idempotent(idempotencyKey))))
    return { runId: String(raw.runId ?? '') }
  },

  // Наборы (этап 3).
  async listKits(jobId: string): Promise<Kit[]> {
    const raw = unwrapGingerbreadResponse<unknown>(await api.get(`${BASE}/jobs/${jobId}/kits`))
    const record = asRecord(raw)
    return (Array.isArray(raw) ? raw : Array.isArray(record.kits) ? record.kits : Array.isArray(record.items) ? record.items : []).map(toKit)
  },
  /** Без `gingerbreadIds` набор собирается из всех принятых пряников задания по позиции. */
  async createKit(jobId: string, body: { name?: string; gingerbreadIds?: string[] } = {}): Promise<Kit> {
    return toKit(unwrapGingerbreadResponse(await api.post(`${BASE}/jobs/${jobId}/kits`, body)))
  },
  async patchKit(id: string, patch: KitPatch): Promise<Kit> {
    return toKit(unwrapGingerbreadResponse(await api.patch(`${BASE}/kits/${id}`, patch)))
  },
  /** 202: фоновая задача сборки листа набора. */
  async renderKit(id: string): Promise<{ jobId: string }> {
    const raw = asRecord(unwrapGingerbreadResponse(await api.post(`${BASE}/kits/${id}/render`, {})))
    return { jobId: String(raw.jobId ?? '') }
  },
  async archiveKit(id: string): Promise<void> {
    await api.post(`${BASE}/kits/${id}/archive`, {})
  },
  async downloadKitFile(id: string, kind: KitFileKind, fileName: string): Promise<void> {
    saveBlob((await api.get(`${BASE}/kits/${id}/files/${kind}`, { responseType: 'blob' })).data as Blob, fileName)
  },

  // Выгрузка в товары (этап 3).
  async previewExport(jobId: string): Promise<ExportPreview> {
    return toExportPreview(unwrapGingerbreadResponse(await api.post(`${BASE}/jobs/${jobId}/export/preview`, {})))
  },
  /** 202: фоновая выгрузка. `titles` — только правки названий; остальное сервер соберёт сам. */
  async startExport(jobId: string, titles?: ExportTitles): Promise<{ jobId: string }> {
    const raw = asRecord(unwrapGingerbreadResponse(await api.post(`${BASE}/jobs/${jobId}/export`, titles ? { titles } : {})))
    return { jobId: String(raw.jobId ?? '') }
  },
}
